import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Player implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private speed: number = 200; // pixels per second
  private canShoot: boolean = true;
  private shootCooldown: number = 0;
  private maxShootCooldown: number = 0.2; // 5 shots per second max
  private gameWidth: number;
  
  // Animation
  private animationTime: number = 0;

  constructor(
    id: string, 
    x: number, 
    y: number, 
    renderManager: RenderManager, 
    audioManager: AudioManager,
    gameWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
  }

  public update(deltaTime: number): void {
    // Update animation
    this.animationTime += deltaTime;
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Keep player within screen bounds
    this.position.x = Math.max(0, Math.min(this.gameWidth - this.size.x, this.position.x));
    
    // Update shoot cooldown
    if (this.shootCooldown > 0) {
      this.shootCooldown -= deltaTime;
      if (this.shootCooldown <= 0) {
        this.canShoot = true;
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Draw player ship (classic space invaders style)
    this.drawPlayerShip(centerX, centerY);
    
    // Add subtle pulsing effect
    if (Math.sin(this.animationTime * 4) > 0.5) {
      this.renderManager.drawPixel(centerX, centerY - 8, '#FFFFFF');
    }
  }

  private drawPlayerShip(centerX: number, centerY: number): void {
    const color = '#62D0FE'; // C64 light blue
    
    // Ship body (triangle pointing up)
    const shipPattern = [
      [0,0,0,1,0,0,0],
      [0,0,1,1,1,0,0],
      [0,1,1,1,1,1,0],
      [1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1]
    ];
    
    // Draw the ship pattern
    for (let row = 0; row < shipPattern.length; row++) {
      for (let col = 0; col < shipPattern[row].length; col++) {
        if (shipPattern[row][col]) {
          const x = centerX - 7 + col * 2;
          const y = centerY - 6 + row * 2;
          this.renderManager.fillRect(x, y, 2, 2, color);
        }
      }
    }
  }

  public moveLeft(): void {
    this.velocity.x = -this.speed;
  }

  public moveRight(): void {
    this.velocity.x = this.speed;
  }

  public stopMoving(): void {
    this.velocity.x = 0;
  }

  public canFireBullet(): boolean {
    return this.canShoot && this.active;
  }

  public fireBullet(): Vector2 | null {
    if (!this.canFireBullet()) return null;
    
    this.canShoot = false;
    this.shootCooldown = this.maxShootCooldown;
    
    // Play shoot sound
    this.audioManager.playShoot();
    
    // Return bullet spawn position
    return {
      x: this.position.x + this.size.x / 2 - 1, // Center the bullet
      y: this.position.y - 2 // Spawn just above the ship
    };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
    // Play explosion sound
    this.audioManager.playExplosion();
  }

  public getCenter(): Vector2 {
    return {
      x: this.position.x + this.size.x / 2,
      y: this.position.y + this.size.y / 2
    };
  }
}